import { ComponentType, Dispatch, SetStateAction, useState } from 'react';

import { cn } from '@virginexperiencedays/tailwind';
import type { Location } from '@virginexperiencedays/products';

import { IconAngleRight } from '../../../icons/IconAngleRight';
import { IconLocationPin } from '../../../icons/IconLocationPin';
import { Columns } from '../../../layout/Columns';
import { LocationsList } from '../../../lists/Locations';
import { BodyText } from '../../../typography/BodyText';
import { DisplayHeading } from '../../../typography/DisplayHeading';

import { reduce as groupLocationsByRegion } from './reduce';

export type LocationsProps = {
  className?: string;
  /**
   * ID is used for link from review summary
   */
  id: string;
  dataTestId?: string;
  locations: Location[];
  MapComponent: ComponentType<{
    GMAP_PUBLIC_API_KEY: string;
    activeRegion: string;
    regions: { [key: string]: Location[] };
    isLocationsVisible: boolean;
  }>;
  GMAP_PUBLIC_API_KEY: string;
  isLocationsVisible: boolean;
};

export type Regions = { [regionName: string]: Location[] };

type FiltersArgs = {
  regions: Regions;
  setActiveRegion: Dispatch<SetStateAction<string>>;
};

type FiltersDesktopArgs = {
  activeRegion: string;
} & FiltersArgs;

export const Locations = ({
  className,
  locations,
  id,
  GMAP_PUBLIC_API_KEY,
  isLocationsVisible,
  dataTestId = 'locations',
  MapComponent = null,
}: LocationsProps) => {
  const [activeRegion, setActiveRegion] = useState<string>('');
  const [viewAsList, setViewAsList] = useState<boolean>(false);

  if (!locations?.length) return null;

  const numLocations = locations.length;

  const regions: Regions = groupLocationsByRegion(locations);

  if (!Object.keys(regions).length) return null;

  const multiLocations = numLocations > 1;

  return (
    <div className={className} data-testid={dataTestId} id={id}>
      <DisplayHeading size="3" as="h2" className="text-neutral-strong mb-6 mt-1 text-xl">
        Locations
      </DisplayHeading>
      <Columns
        className="items-center justify-between"
        collapseBelow={null}
        widths={['max-content']}
        space="xs"
      >
        <IconLocationPin />
        {(numLocations < 2 && <NumOfLocationsText>{locations[0].name}</NumOfLocationsText>) || (
          <NumOfLocationsText>{numLocations} locations across the UK</NumOfLocationsText>
        )}
      </Columns>

      <div
        className={cn(
          "mt-4 flex flex-col gap-4 md:grid md:grid-cols-[auto_1fr] md:grid-rows-[1fr_auto] md:[grid-template-areas:'filters_map'_'locations_locations']",
          !multiLocations ? 'gap-x-0' : null
        )}
        id={id}
      >
        {numLocations > 1 && (
          <div className="[grid-area:filters] md:min-w-[320px]">
            <RegionFiltersDesktop
              regions={regions}
              activeRegion={activeRegion}
              setActiveRegion={setActiveRegion}
            />
            <RegionFiltersMobile regions={regions} setActiveRegion={setActiveRegion} />
          </div>
        )}
        {MapComponent && (
          <div
            className={cn('flex-1 [grid-area:map]', numLocations > 1 ? 'max-w-[940px]' : 'w-auto')}
          >
            <MapComponent
              isLocationsVisible={isLocationsVisible}
              regions={regions}
              activeRegion={activeRegion}
              GMAP_PUBLIC_API_KEY={GMAP_PUBLIC_API_KEY}
            />
            <BodyText
              className="text-grey-400 mt-4 flex flex-col flex-nowrap justify-end gap-2 text-right text-xs md:flex-row md:flex-wrap"
              size="small"
            >
              <span>Locations shown shouldn&apos;t be used for route planning</span>
              {activeRegion && (
                <>
                  <span className="hidden md:inline">|</span>
                  <button
                    className="cursor-pointer border-0 bg-transparent p-0 text-right text-xs hover:underline"
                    onClick={() => setViewAsList((p) => !p)}
                  >
                    {viewAsList ? 'Hide List' : 'View As List'}
                  </button>
                </>
              )}
            </BodyText>
          </div>
        )}
        <LocationsList
          className="[grid-area:locations]"
          viewAsList={viewAsList}
          activeRegion={activeRegion}
          regions={regions}
        />
      </div>
    </div>
  );
};

const RegionFiltersDesktop = ({ regions, activeRegion, setActiveRegion }: FiltersDesktopArgs) => {
  const names = Object.keys(regions);

  return (
    <ul className="hidden md:-mx-2 md:flex md:flex-col">
      {names.map((name) => {
        const locations = regions[name];
        const isActive = name === activeRegion;
        return (
          <li key={`region-filter-desktop-${name}`}>
            <button
              className={cn(
                'text-neutral flex h-10 w-full cursor-pointer items-center justify-between rounded px-2',
                !isActive && 'hover:bg-background-primary-faded',
                isActive && 'bg-background-primary text-white'
              )}
              onClick={() => setActiveRegion(name)}
            >
              <div className="flex">
                <span className="text-sm font-semibold leading-none">{name}</span>
                <span className="ml-1 text-xs font-normal leading-snug">
                  ({locations.length} locations)
                </span>
              </div>
              <IconAngleRight width="10" height="10" />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const RegionFiltersMobile = ({ regions, setActiveRegion }: FiltersArgs) => {
  const regionNames = Object.keys(regions);

  return (
    <div className="md:hidden">
      <select
        className={cn(
          'border-border-neutral bg-background-page rounded border',
          'text-neutral-strong no-appearance w-full p-4 text-sm leading-none'
        )}
        id="regions"
        onChange={(event) => setActiveRegion(event.target.value)}
      >
        <option disabled>Select a region...</option>
        <option value="">All UK</option>
        {regionNames.map((name) => {
          const locations = regions[name];

          return (
            <option key={name} value={name}>
              {name} ({locations.length})
            </option>
          );
        })}
      </select>
    </div>
  );
};

const NumOfLocationsText = ({ children }) => (
  <BodyText className="text-neutral">{children}</BodyText>
);
